import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { RevealGroup } from '@/components/atoms';
import { SectionHeading } from '@/components/molecules';
import { Section } from '@/components/organisms';
import { introVehicles } from '@/config/introVehicles';
import { anchorOffsetSx } from '@/theme';

import { COMPARE_ID, COMPARE_TITLE_ID, compareColumns, serviceCountWord } from '../content';
import { useServiceComparison } from '../hooks/useServiceComparison';
import { compareGridSx, ServiceCompareRow } from '../molecules';

/**
 * Every service on one screen, so they can be chosen between rather than read
 * in sequence.
 *
 * The band the catalogue above it cannot be. Cards are the right shape for
 * "tell me about this one" and the wrong shape for "which one of these" — a
 * reader comparing seven cards is holding six of them in their head, and the
 * two facts they are comparing on (what it leaves me with, what it costs) are
 * the two a card has the least room for. Here those are columns, every
 * service is one line, and the whole set is a scan rather than a read.
 *
 * It sits after the catalogue, not before it. The catalogue is the page's
 * argument — that an import has an order and these services are what the
 * order is made of — and a table in front of it would reduce that to a
 * price list before the reader had been given the shape. Browse, then decide.
 *
 * ## Why the band is flat and textured rather than toned
 *
 * This page alternates default/muted, and the catalogue above and the
 * situations band below already hold those two. Rather than break the
 * alternation for three bands, this one takes the default tone and carries
 * its boundary with a pattern and a backdrop — the same trade
 * `CompanyStoryBand` makes further down the page, and documented there.
 *
 * ## The header row
 *
 * `aria-hidden`, and the rows are a list of links rather than a `<table>`.
 * The reasoning is in `ServiceCompareRow`: the headings align the columns
 * from `md` up and carry no meaning the cells do not already carry, so below
 * `md` — where the cells stack and a heading would be a screen away from what
 * it labels — the row is simply not drawn.
 */
export function ServiceCompareBand() {
  const rows = useServiceComparison();

  return (
    <Section
      id={COMPARE_ID}
      labelledBy={COMPARE_TITLE_ID}
      pattern="diagonals"
      backdrop={{ picture: introVehicles.impreza.image, placement: 'right' }}
      sx={anchorOffsetSx(96, 96)}
    >
      <SectionHeading
        id={COMPARE_TITLE_ID}
        eyebrow="At a glance"
        title="All of it on one screen"
        lede={`What each of the ${serviceCountWord} leaves you holding, and what it costs you. Every row opens the page behind it.`}
      />

      {/* The column headings. Decoration: see the band's note. */}
      <Box
        aria-hidden
        sx={{
          ...compareGridSx,
          display: { xs: 'none', md: 'grid' },
          px: 3,
          pb: 3,
        }}
      >
        {Object.values(compareColumns).map((heading) => (
          <Typography
            key={heading}
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontWeight: 700,
              // `text.disabled` measured 2.9:1 here, which is a fail at 12px.
              // A heading nobody can read is not a quieter heading.
              color: 'text.secondary',
            }}
          >
            {heading}
          </Typography>
        ))}
      </Box>

      <RevealGroup component="ul" step={40} sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {rows.map((row) => (
          <ServiceCompareRow key={row.service.slug} row={row} />
        ))}
      </RevealGroup>
    </Section>
  );
}
